import { refDebounced } from '@vueuse/core'
import { orderBy } from 'es-toolkit/array'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import type {
  JobGroup,
  JobListItem,
  JobSortMode,
  JobTab
} from '@/composables/queue/useJobList'
import {
  useNordyQueueStore,
  NordyTaskDisplayStatus
} from '@/nordy/store/nordyQueueStore'
import type { NordyQueue } from '@/nordy/store/nordyQueueStore'
import { useNordyWorkflowStore } from '@/nordy/store/nordyWorkflowStore'
import type { TaskItemImpl } from '@/stores/queueStore'
import type { JobState } from '@/types/queue'
import {
  dateKey,
  formatClockTime,
  formatShortMonthDay,
  isToday,
  isYesterday
} from '@/utils/dateTimeUtil'
import { formatDuration } from '@/utils/formatUtil'
import { iconForJobState } from '@/utils/queueDisplay'

const relativeTimeFormatterCache = new Map<string, Intl.RelativeTimeFormat>()

function dateLabelForTimestamp(
  ts: number,
  locale: string,
  relativeFormatter: Intl.RelativeTimeFormat
): string {
  const formatRelativeDay = (value: number) => {
    const formatted = relativeFormatter.format(value, 'day')
    return formatted
      ? formatted[0].toLocaleUpperCase(locale) + formatted.slice(1)
      : formatted
  }
  if (isToday(ts)) return formatRelativeDay(0)
  if (isYesterday(ts)) return formatRelativeDay(-1)
  return formatShortMonthDay(ts, locale)
}

function nordyJobState(task: NordyQueue): JobState {
  switch (task.displayStatus) {
    case NordyTaskDisplayStatus.Running:
      return 'running'
    case NordyTaskDisplayStatus.Pending:
    case NordyTaskDisplayStatus.Queued:
      return 'pending'
    case NordyTaskDisplayStatus.Completed:
      return 'completed'
    case NordyTaskDisplayStatus.Failed:
    case NordyTaskDisplayStatus.Cancelled:
    case NordyTaskDisplayStatus.ExecutionTimeout:
      return 'failed'
  }
}

function buildNordyJobDisplay(
  task: NordyQueue,
  state: JobState,
  t: (k: string, v?: Record<string, unknown>) => string,
  locale: string
): {
  iconName: string
  iconImageUrl?: string
  primary: string
  secondary: string
  showClear: boolean
} {
  const clockTime = task.createdAt
    ? formatClockTime(task.createdAt.getTime(), locale)
    : ''

  if (state === 'pending') {
    //!nordy - Queued 상태(백엔드 Job 생성됨)에서는 cancel 불가, Pending만 cancel 허용
    return {
      iconName: iconForJobState(state),
      primary: task.isQueued ? t('queue.inQueue') : t('queue.inQueue'),
      secondary: clockTime,
      showClear: !task.isQueued //!nordy - Queued 상태에서는 cancel 불가
    }
  }
  if (state === 'running') {
    return {
      iconName: iconForJobState(state),
      primary: t('g.running'),
      secondary: clockTime,
      showClear: false //!nordy - Running 상태에서는 cancel 불가 (백엔드 Job이 실행 중)
    }
  }
  if (state === 'completed') {
    const seconds = task.executionTimeInSeconds
    const preview = task.previewOutput
    const iconImageUrl = preview && preview.isImage ? preview.url : undefined
    const primary = t('queue.completedIn', {
      duration: formatDuration((seconds ?? 0) * 1000)
    })
    return {
      iconName: iconForJobState(state),
      iconImageUrl,
      primary,
      secondary: seconds !== undefined ? `${seconds.toFixed(2)}s` : '',
      showClear: false
    }
  }
  // failed / cancelled / timeout
  const isTimeout =
    task.displayStatus === NordyTaskDisplayStatus.ExecutionTimeout
  return {
    iconName: iconForJobState(state),
    primary: isTimeout ? 'Execution Timeout' : t('g.failed'),
    secondary: t('g.failed'),
    showClear: true
  }
}

/**
 * Nordy-specific version of useJobList.
 * Reads from useNordyQueueStore instead of useQueueStore.
 */
export function useNordyJobList() {
  const { t, locale } = useI18n()
  const queueStore = useNordyQueueStore()
  const workflowStore = useNordyWorkflowStore()

  const selectedJobTab = ref<JobTab>('All')
  const selectedWorkflowFilter = ref<'all' | 'current'>('all')
  const selectedSortMode = ref<JobSortMode>('mostRecent')
  const searchQuery = ref('')
  const debouncedSearchQuery = refDebounced(searchQuery, 150)

  const taskTimestamp = (task: NordyQueue): number =>
    task.createdAt?.getTime() ?? 0

  const taskSeq = (task: NordyQueue): number => task.seq ?? 0

  const allTasksSorted = computed<NordyQueue[]>(() =>
    orderBy([...queueStore.tasks], [taskTimestamp, taskSeq], ['desc', 'desc'])
  )

  type TaskWithState = { task: NordyQueue; state: JobState }

  const tasksWithJobState = computed<TaskWithState[]>(() =>
    allTasksSorted.value.map((task) => ({
      task,
      state: nordyJobState(task)
    }))
  )

  const hasFailedJobs = computed(() =>
    tasksWithJobState.value.some(({ state }) => state === 'failed')
  )

  const filteredTaskEntries = computed<TaskWithState[]>(() => {
    let entries = tasksWithJobState.value
    if (selectedJobTab.value === 'Completed') {
      entries = entries.filter(({ state }) => state === 'completed')
    } else if (selectedJobTab.value === 'Failed') {
      entries = entries.filter(({ state }) => state === 'failed')
    }

    if (selectedWorkflowFilter.value === 'current') {
      const activeUid = workflowStore.activeWorkflow?.uid
      if (!activeUid) return []
      entries = entries.filter(
        ({ task }) => !!task.workflowUid && task.workflowUid === activeUid
      )
    }
    return entries
  })

  const normalizedSearchQuery = computed(() =>
    debouncedSearchQuery.value.trim().toLocaleLowerCase()
  )

  const relativeTimeFormatter = computed(() => {
    const localeValue = locale.value
    let formatter = relativeTimeFormatterCache.get(localeValue)
    if (!formatter) {
      formatter = new Intl.RelativeTimeFormat(localeValue, { numeric: 'auto' })
      relativeTimeFormatterCache.set(localeValue, formatter)
    }
    return formatter
  })

  const undatedLabel = computed(() => t('queue.jobList.undated') || 'Undated')

  const jobItems = computed<JobListItem[]>(() =>
    filteredTaskEntries.value.map(({ task, state }) => {
      const display = buildNordyJobDisplay(task, state, t, locale.value)
      const seconds = task.executionTimeInSeconds
      return {
        id: task.jobId ?? task.id,
        title: display.primary,
        meta: display.secondary,
        state,
        iconName: display.iconName,
        iconImageUrl: display.iconImageUrl,
        showClear: display.showClear,
        taskRef: task as unknown as TaskItemImpl,
        progressTotalPercent: undefined,
        progressCurrentPercent: undefined,
        runningNodeName: undefined,
        executionTimeMs: seconds !== undefined ? seconds * 1000 : undefined,
        computeHours: seconds !== undefined ? seconds / 3600 : undefined
      } satisfies JobListItem
    })
  )

  const jobItemById = computed(() => {
    const m = new Map<string, JobListItem>()
    jobItems.value.forEach((ji) => m.set(ji.id, ji))
    return m
  })

  const searchableTaskEntries = computed<TaskWithState[]>(() => {
    if (!normalizedSearchQuery.value) return filteredTaskEntries.value

    return filteredTaskEntries.value.filter(({ task }) => {
      const taskId = (task.jobId ?? task.id).toLocaleLowerCase()
      const item = jobItemById.value.get(task.jobId ?? task.id)
      if (!item) return taskId.includes(normalizedSearchQuery.value)

      const title = item.title.toLocaleLowerCase()
      const meta = item.meta.toLocaleLowerCase()
      return (
        title.includes(normalizedSearchQuery.value) ||
        meta.includes(normalizedSearchQuery.value) ||
        taskId.includes(normalizedSearchQuery.value)
      )
    })
  })

  const filteredTasks = computed<NordyQueue[]>(() =>
    searchableTaskEntries.value.map(({ task }) => task)
  )

  const groupedJobItems = computed<JobGroup[]>(() => {
    const groups: JobGroup[] = []
    const index = new Map<string, number>()
    const localeValue = locale.value

    for (const { task, state } of searchableTaskEntries.value) {
      let ts: number | undefined
      if (state === 'completed' || state === 'failed') {
        ts = task.job?.endAt
          ? new Date(task.job.endAt).getTime()
          : task.createdAt?.getTime()
      } else {
        ts = task.createdAt?.getTime()
      }
      const key = ts === undefined ? 'undated' : dateKey(ts)
      let groupIdx = index.get(key)
      if (groupIdx === undefined) {
        const label =
          ts === undefined
            ? undatedLabel.value
            : dateLabelForTimestamp(
                ts,
                localeValue,
                relativeTimeFormatter.value
              )
        groups.push({ key, label, items: [] })
        groupIdx = groups.length - 1
        index.set(key, groupIdx)
      }
      const ji = jobItemById.value.get(task.jobId ?? task.id)
      if (ji) groups[groupIdx].items.push(ji)
    }

    if (selectedSortMode.value === 'totalGenerationTime') {
      const valueOrDefault = (value: JobListItem['executionTimeMs']) =>
        typeof value === 'number' && !Number.isNaN(value) ? value : -1
      const sortByExecutionTimeDesc = (a: JobListItem, b: JobListItem) =>
        valueOrDefault(b.executionTimeMs) - valueOrDefault(a.executionTimeMs)
      groups.forEach((group) => {
        group.items.sort(sortByExecutionTimeDesc)
      })
    }

    return groups
  })

  return {
    selectedJobTab,
    selectedWorkflowFilter,
    selectedSortMode,
    searchQuery,
    hasFailedJobs,
    filteredTasks,
    groupedJobItems
  }
}
